import { ADMIN_FUNCTIONS } from '../../config/functionRoutes';
import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Checkbox,
  Box,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import { ActionRequest } from '../../types';

// Extend ActionRequest with registration details
interface ActionRequestWithReg extends ActionRequest {
  firstName?: string;
  lastName?: string;
  registrationNumber?: number;
}

const ActionRequestsPanel: React.FC = () => {
  const [requests, setRequests] = useState<ActionRequestWithReg[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'actionRequests'), where('status', '==', 'pending'));
    const unsub = onSnapshot(q, snap => {
      (async () => {
        const items = await Promise.all(
          snap.docs.map(async d => {
            const req = d.data() as ActionRequest;
            const regSnap = await getDoc(doc(db, 'registrations', req.registrationId));
            const regData = regSnap.data();
            return {
              id: d.id,
              ...req,
              firstName: regData?.firstName,
              lastName: regData?.lastName,
              registrationNumber: regData?.registrationNumber,
            };
          })
        );
        setRequests(items);
      })();
    });
    return () => unsub();
  }, []);

  const setProcessing = useCallback((id: string, isProcessing: boolean) => {
    setProcessingIds(prev => {
      const next = new Set(prev);
      if (isProcessing) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleApprove = async (req: ActionRequestWithReg) => {
    if (!req.id) return;
    setProcessing(req.id, true);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const processActionRequest = httpsCallable(functions, ADMIN_FUNCTIONS.actionRequest);
      await processActionRequest({ actionRequestId: req.id });
    } catch (err) {
      console.error('Error processing action request:', err);
    } finally {
      setProcessing(req.id, false);
    }
  };

  const handleReject = async (req: ActionRequestWithReg) => {
    const reqRef = doc(db, 'actionRequests', req.id!);
    await updateDoc(reqRef, { status: 'rejected', actedAt: serverTimestamp() });
  };

  // bulk action handlers
  const handleBulkApprove = async () => {
    const toApprove = requests.filter(r => selected.has(r.id!));
    if (toApprove.length === 0) return;

    const functions = getFunctions(undefined, 'europe-west1');
    const processActionRequest = httpsCallable(functions, ADMIN_FUNCTIONS.actionRequest);

    setBulkProcessing(true);
    toApprove.forEach(req => req.id && setProcessing(req.id, true));

    try {
      await Promise.all(toApprove.map(req => processActionRequest({ actionRequestId: req.id })));
    } catch (err) {
      console.error('Error processing bulk approvals:', err);
    } finally {
      toApprove.forEach(req => req.id && setProcessing(req.id, false));
      setBulkProcessing(false);
      setSelected(new Set());
    }
  };

  const handleBulkReject = async () => {
    const toReject = requests.filter(r => selected.has(r.id!));
    for (const req of toReject) { await handleReject(req); }
    setSelected(new Set());
  };

  return (
    <>
      {(bulkProcessing || processingIds.size > 0) && (
        <Box mb={2}>
          <LinearProgress />
        </Box>
      )}
      <Box mb={2} display="flex" gap={1}>
        <Button
          size="small"
          onClick={handleBulkApprove}
          disabled={selected.size === 0 || bulkProcessing}
        >
          {bulkProcessing ? 'Approving…' : 'Approve Selected'}
        </Button>
        <Button size="small" onClick={handleBulkReject} disabled={selected.size === 0}>Reject Selected</Button>
      </Box>
      <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && selected.size < requests.length}
                  checked={requests.length > 0 && selected.size === requests.length}
                  onChange={e => {
                    if (e.target.checked) setSelected(new Set(requests.map(r => r.id!)));
                    else setSelected(new Set());
                  }}
                />
              </TableCell>
              <TableCell>Reg #</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map(r => {
              const isProcessing = r.id ? processingIds.has(r.id) : false;
              return (
                <TableRow key={r.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(r.id!)}
                      onChange={() => {
                        const newSel = new Set(selected);
                        if (newSel.has(r.id!)) newSel.delete(r.id!);
                        else newSel.add(r.id!);
                        setSelected(newSel);
                      }}
                    />
                  </TableCell>
                  <TableCell>{r.registrationNumber}</TableCell>
                  <TableCell>{r.firstName} {r.lastName}</TableCell>
                  <TableCell>{r.type}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.reason}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleApprove(r)}
                      disabled={isProcessing || bulkProcessing}
                    >
                      {isProcessing ? (
                        <>
                          Processing
                          <CircularProgress size={16} sx={{ ml: 1 }} />
                        </>
                      ) : 'Approve'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleReject(r)}
                      disabled={isProcessing || bulkProcessing}
                    >
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ActionRequestsPanel;
